/**
 * Раскладка начальных данных NFT-айтема.
 *
 * §9.1 держится на том, что мастер считает адрес экземпляра сам: спросить
 * коллекцию синхронно в TON нельзя, а верить адресу из запроса — значит
 * принимать в залог что угодно. Но посчитать адрес можно, только зная, что
 * коллекция кладёт в data при развёртывании, а кладут все по-разному:
 * эталонная TEP-62 — (index: uint64, collection), TON DNS — 256-битный
 * индекс, гифты Fragment — ссылку на статику.
 *
 * Поэтому раскладка не угадывается на ходу, а определяется один раз, при
 * внесении коллекции в реестр: берётся настоящий айтем, и адрес считается
 * каждой раскладкой по очереди. Сошёлся — значит эта. Не сошлась ни одна —
 * коллекцию вносить нельзя: сделку по ней всё равно не создать.
 */

import { Address, Cell, beginCell, contractAddress } from '@ton/core';
import {
  ITEM_LAYOUT_INDEX256,
  ITEM_LAYOUT_INDEX64,
  ITEM_LAYOUT_REF256,
  type ItemLayout,
} from './opcodes.ts';

/** Глубина шарда у эталонного jetton-кошелька — та же, что в контракте. */
const JETTON_SHARD_DEPTH = 8;

/**
 * Адрес контракта, развёрнутого «рядом с владельцем».
 *
 * TON умеет держать кошелёк в том же шарде, что и его владельца: у StateInit
 * выставляется split_depth, а старшие биты адреса берутся у владельца. Расчёт
 * повторяет то, что делает контракт, и сверен с ним в тестах.
 */
function shardedAddress(code: Cell, data: Cell, closeTo: Address): Address {
  const stateInit = beginCell()
    .storeBit(1)
    .storeUint(JETTON_SHARD_DEPTH, 5)
    .storeBit(0)
    .storeBit(1)
    .storeRef(code)
    .storeBit(1)
    .storeRef(data)
    .storeBit(0)
    .endCell();

  const hash = Buffer.from(stateInit.hash());
  closeTo.hash.copy(hash, 0, 0, JETTON_SHARD_DEPTH / 8);
  return new Address(0, hash);
}

/** Те же раскладки, что в contracts/types.tolk — поле в поле. */
export function itemData(layout: ItemLayout, index: bigint, collection: Address): Cell {
  if (layout === ITEM_LAYOUT_INDEX256) {
    return beginCell().storeUint(index, 256).storeAddress(collection).endCell();
  }
  if (layout === ITEM_LAYOUT_REF256) {
    const stat = beginCell().storeUint(index, 256).storeAddress(collection).endCell();
    return beginCell().storeUint(0, 1).storeRef(stat).endCell();
  }
  return beginCell().storeUint(index, 64).storeAddress(collection).endCell();
}

export function itemAddress(
  layout: ItemLayout,
  code: Cell,
  index: bigint,
  collection: Address,
): Address {
  return contractAddress(0, { code, data: itemData(layout, index, collection) });
}

/**
 * Какая раскладка у этой коллекции — по одному настоящему айтему.
 * null означает «ни одна из известных», и это ответ, а не сбой.
 */
export function detectItemLayout(opts: {
  code: Cell;
  index: bigint;
  collection: Address;
  item: Address;
}): ItemLayout | null {
  const layouts: ItemLayout[] = [ITEM_LAYOUT_INDEX64, ITEM_LAYOUT_INDEX256, ITEM_LAYOUT_REF256];
  for (const layout of layouts) {
    // Индекс шире 64 бит в эталонную раскладку просто не помещается.
    if (layout === ITEM_LAYOUT_INDEX64 && opts.index >= 1n << 64n) continue;
    if (itemAddress(layout, opts.code, opts.index, opts.collection).equals(opts.item)) {
      return layout;
    }
  }
  return null;
}


/**
 * Дружественная запись адреса.
 *
 * Внутри всё сырое — `0:bace389d…`, — и это правильно: одна форма, один ключ.
 * Но человеку показывать её нельзя: в кошельках, эксплорерах и на торговых
 * площадках адрес выглядит иначе, и сверить их глазами невозможно.
 *
 * `wallet: true` даёт небounceable-форму (UQ…) — так кошельки показывают
 * себя сами. Не адрес возвращается как есть: ронять экран из-за строки
 * незачем.
 */
export function friendlyAddress(address: string, opts: { wallet?: boolean } = {}): string {
  try {
    return Address.parse(address).toString({ bounceable: opts.wallet !== true });
  } catch {
    return address;
  }
}
